import { DatePicker } from '@/components/DatePicker'
import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetQuotation, useUpdateQuotation } from '@/features/quotations/hooks/useQuotation'
import { getMutationOptions } from '@/utils/mutation'
import { AlertCircle, Building2, Calendar, CheckCircle2, Package } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function QuoteFormContent({ quatationId }: { quatationId: string }) {
  const { t } = useTranslation()
  const { isVendor } = useAuth()
  const { data: quotation, isFetching: isFetchingQuote } = useGetQuotation(quatationId)
  const updateQuotation = useUpdateQuotation()

  const [submitType, setSubmitType] = useState<'save' | 'submit' | null>(null)

  const [form, setForm] = useState({
    price: '',
    minQuantity: '1',
    validUntil: '',
    warrantyYears: '2',
    maintenancePricePerYear: '',
    note: '',
  })

  useEffect(() => {
    if (quotation) {
      setForm({
        price: quotation.price?.toString() ?? '',
        minQuantity: quotation.minQuantity?.toString() ?? '1',
        validUntil: quotation.validUntil ?? '',
        warrantyYears: quotation.warrantyYears?.toString() ?? '2',
        maintenancePricePerYear: quotation.maintenancePricePerYear?.toString() ?? '',
        note: quotation.note ?? '',
      })
    }
  }, [quotation])

  const isLoading = isFetchingQuote || updateQuotation.isPending
  const isInvalid = !form.price || !form.validUntil || !form.maintenancePricePerYear

  function handleSubmit(isSubmit = false) {
    if (isInvalid) return
    setSubmitType(isSubmit ? 'submit' : 'save')

    const options = getMutationOptions({
      successMessage: isSubmit ? t('quotation.toast.submitSuccess') : t('quotation.toast.updateSuccess'),
      onSuccess: () => {
        setSubmitType(null)
      },
    })

    const payload = {
      ...form,
      price: Number(form.price),
      minQuantity: Number(form.minQuantity),
      warrantyYears: Number(form.warrantyYears),
      maintenancePricePerYear: Number(form.maintenancePricePerYear),
      submit: isSubmit,
    }

    updateQuotation.mutate({ id: quatationId, payload }, options)
  }

  if (isFetchingQuote) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <p className="mt-1 text-sm text-muted-foreground">{t('common.loading')}</p>
      </div>
    )
  }

  if (!quotation) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <Card className="w-full max-w-md border-destructive/50 bg-card">
          <CardContent className="flex flex-col items-center gap-4 py-10">
            <AlertCircle className="size-12 text-destructive" />
            <h1 className="text-lg font-semibold text-foreground">{t('quotation.notFound.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('quotation.notFound.description')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (quotation.status === 'Submitted') {
    return (
      <div data-testid="quotation-submitted-view" className="flex items-center justify-center min-h-[80vh]">
        <Card className="w-full max-w-md border-success/50 bg-card">
          <CardContent className="flex flex-col items-center gap-4 py-10">
            <CheckCircle2 className="size-12 text-success" />
            <h1 className="text-lg font-semibold text-foreground">{t('quotation.submitted.title')}</h1>
            <p className="text-sm text-muted-foreground text-center">
              {/* ขอบคุณที่ส่งใบเสนอราคาสำหรับ {product.categoryName} */}
              <br />
              {t('quotation.submitted.description')}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-lg p-4 space-y-4">
      {/* Product Info Card */}
      <SectionCard
        isLoading={isFetchingQuote}
        title={t('quotation.requestTitle')}
        description={t('quotation.requestDescription')}
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3">
            <Package className="mt-0.5 size-4 text-primary" />

            <div className="flex-1">
              {/* <span className="text-sm font-semibold text-foreground">{quotation?.productName}</span> */}
              {quotation?.productName ? (
                <>
                  <span className="text-sm font-semibold text-foreground">{quotation?.productName}</span>
                  <p className="text-xs text-muted-foreground">
                    {quotation?.productBrand} {quotation?.productModel}
                  </p>
                </>
              ) : (
                <span className="text-sm font-semibold text-foreground">
                  {quotation?.productBrand} {quotation?.productModel}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3">
            <Building2 className="size-4 text-primary" />
            <div>
              <span className="text-[10px] text-muted-foreground">{t('quotation.vendor')}</span>
              <p className="text-xs font-medium text-foreground">{quotation.vendorName || '-'}</p>
            </div>
          </div>

          {quotation.adminNote && (
            <div className="rounded-lg border border-border/30 bg-secondary/30 p-3">
              <span className="text-[10px] text-muted-foreground">{t('quotation.requesterNote')}</span>
              <p className="text-xs text-foreground">{quotation.adminNote ?? '-'} </p>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Quote Form */}
      <SectionCard isLoading={isFetchingQuote} title={t('quotation.form.title')}>
        {!isVendor ? (
          <span className="text-sm text-muted-foreground">{t('quotation.vendorOnly')}</span>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">{t('quotation.form.pricePerUnit')}</Label>

                <Input
                  data-testid="quotation-form-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={e =>
                    setForm({
                      ...form,
                      price: e.target.value,
                    })
                  }
                  className="h-9 bg-secondary/50 text-xs"
                  placeholder={t('quotation.form.placeholder.price')}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">{t('quotation.form.minQuantity')}</Label>

                <Input
                  data-testid="quotation-form-min-quantity"
                  type="number"
                  min="1"
                  value={form.minQuantity}
                  onChange={e =>
                    setForm({
                      ...form,
                      minQuantity: e.target.value,
                    })
                  }
                  className="h-9 bg-secondary/50 text-xs"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">{t('quotation.form.validUntil')}</Label>

                <DatePicker
                  testId="quotation-form-valid-until"
                  value={form.validUntil}
                  onChange={validUntil =>
                    setForm({
                      ...form,
                      validUntil,
                    })
                  }
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">{t('quotation.form.warrantyYears')}</Label>

                <Input
                  data-testid="quotation-form-warranty-years"
                  type="number"
                  min="0"
                  value={form.warrantyYears}
                  onChange={e =>
                    setForm({
                      ...form,
                      warrantyYears: e.target.value,
                    })
                  }
                  className="h-9 bg-secondary/50 text-xs"
                  required
                />
              </div>

              <div className="col-span-1 flex flex-col gap-1.5 sm:col-span-2">
                <Label className="text-xs">{t('quotation.form.maintenancePricePerYear')}</Label>

                <Input
                  data-testid="quotation-form-maintenance-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.maintenancePricePerYear}
                  onChange={e =>
                    setForm({
                      ...form,
                      maintenancePricePerYear: e.target.value,
                    })
                  }
                  className="h-9 bg-secondary/50 text-xs"
                  placeholder={t('quotation.form.placeholder.price')}
                  required
                />
              </div>

              <div className="col-span-1 flex flex-col gap-1.5 sm:col-span-2">
                <Label className="text-xs">{t('quotation.form.additionalNote')}</Label>

                <Textarea
                  data-testid="quotation-form-note"
                  value={form.note}
                  onChange={e =>
                    setForm({
                      ...form,
                      note: e.target.value,
                    })
                  }
                  className="bg-secondary/50 text-xs"
                  rows={3}
                  placeholder={t('quotation.form.placeholder.note')}
                />
              </div>
            </div>

            {isVendor && (
              <div className="flex flex-col gap-1">
                <Button
                  data-testid="quotation-form-save"
                  onClick={() => handleSubmit()}
                  className="mt-2 h-10 w-full text-sm"
                  disabled={isInvalid || isLoading}
                >
                  {submitType === 'save' && updateQuotation.isPending && <Spinner />}
                  {t('common.save')}
                </Button>

                <Button
                  data-testid="quotation-form-submit"
                  onClick={() => handleSubmit(true)}
                  className="mt-2 h-10 w-full text-sm"
                  disabled={isInvalid || isLoading}
                >
                  <Calendar className="mr-2 size-4" />

                  {submitType === 'submit' && updateQuotation.isPending && <Spinner />}
                  {t('quotation.submitButton')}
                </Button>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <p className="mt-4 text-center text-[10px] text-muted-foreground">{t('quotation.footer')}</p>
    </div>
  )
}
